import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import LargeFloatingTextField from "../../components/formFields/largeFloatingText/LargeFloatingTextField";
import Status from "../../enums/Status";
import FormFieldValidation from "../../enums/FormFieldValidation";
import { useEffect, useState } from "react";
import sarService from "../../services/sarService";

interface IRequestAdditionalInformationModalProps {
    show: boolean;
    requestId: string;
    onSubmit: () => void;
    onClose: () => void;
}

export const RequestAdditionalInformationModal = (props: IRequestAdditionalInformationModalProps) => {
    const { t } = useTranslation();
    const { show, requestId, onSubmit, onClose } = props;

    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [requiredInfo, setRequiredInfo] = useState<string>("");
    const [validation, setValidation] = useState<FormFieldValidation>(FormFieldValidation.NOT_VALIDATED);

    useEffect(() => {
        setStatus(Status.NEUTRAL);
        setRequiredInfo("");
        setValidation(FormFieldValidation.NOT_VALIDATED);
    }, [props.show]);

    const handleSubmit = async () => {
        if (!requiredInfo?.trim()) {
            setValidation(FormFieldValidation.INVALID);
            return;
        }

        setStatus(Status.SAVING);
        setValidation(FormFieldValidation.VALID);
        
        const success = await requestInfo(requestId, requiredInfo);

        if (success) {
            setStatus(Status.SAVE_SUCCESS);
            onSubmit();
        } else {
            setStatus(Status.SAVE_ERROR);
        }
    };

    const isDisabled = [Status.SAVING, Status.SAVE_SUCCESS].includes(status);

    return (
        <Modal centered
            backdrop="static"
            show={show}
            onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {t("dsar.requestInfo.header")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row>
                        <Col>
                            <LargeFloatingTextField required
                                height={100}
                                type="textarea"
                                value={requiredInfo}
                                disabled={isDisabled}
                                onChange={(value) => setRequiredInfo(value)}
                                label={t("dsar.requestInfo.form.fields.additionalInfo.label")}
                                validation={validation}
                                validationMessage={t("dsar.requestInfo.form.fields.additionalInfo.validation")}
                                />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {
                                status === Status.SAVING &&
                                <Alert variant="info">
                                    {t("general.pleaseWait")}
                                </Alert>
                            }
                            {
                                status === Status.SAVE_SUCCESS &&
                                <Alert variant="success">
                                    {t("dsar.success.requestInfo")}
                                </Alert>
                            }
                            {
                                status === Status.SAVE_ERROR &&
                                <Alert variant="danger">
                                    {t("dsar.errors.requestInfo")}
                                </Alert>
                            }
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isDisabled}>
                    {t("general.close")}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isDisabled}>
                    {t("general.submit")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

async function requestInfo(id: string, infoRequested: string) {
    try {
        const result = await sarService.requestAdditionalInfo(id, infoRequested);
        return result;
    } catch (e) {
        return false;
    }
}